import { CellContext, Column, ColumnDef, Table } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/Common/DataTable';
import { AcademicTeachingRelatedDutyDetails, TeachingRelatedDutyDetails } from '#server/models/teaching-related-duties';
import { Input, NumberInput } from '@/components/ui';
import { EditableAcademicTeachingRelatedDutyDetails } from './UnitTeachingRelatedDuties';
import { useNavigate } from 'react-router';
import { useAwmsStore } from '@/store/AwmsStore';
import { formatNumber, parseNumericValue } from '@/lib/utils';

export interface CustomTRDColumnMetadata {
  trd: TeachingRelatedDutyDetails | undefined;
  plannedStudents: number;
  taughtWeeks: number;
}

const createHeader = <TData,>(title: string) => {
  return ({ column }: { column: Column<TData, unknown> }) => (
    <DataTableColumnHeader
      column={column}
      title={title}
      className="w-[140px] !border-b-0 pb-0.5"
      titleClassName="text-secondary-grey-02 !text-b3 font-semibold"
    />
  );
};

const getFooterHours = <TData,>(table: Table<TData>, key: string) => {
  // Access the meta property
  const column = table.getAllColumns().find((col) => col.id === key);
  const trdMeta = (column?.columnDef.meta?.custom as CustomTRDColumnMetadata).trd;
  const trdKey = `trd${key[0]?.toUpperCase()}${key.substring(1)}${key.substring(0, key.indexOf('Hours'))}`;

  const totalHours = table
    .getRowModel()
    .rows.reduce(
      (total, row) =>
        total +
        (((row.original as EditableAcademicTeachingRelatedDutyDetails)[
          `${trdKey}Hours` as keyof EditableAcademicTeachingRelatedDutyDetails
        ] as number) ?? 0),
      0,
    );
  const canAssignHours = trdMeta?.[`${trdKey}CanAssignHours` as keyof TeachingRelatedDutyDetails] ?? 0;
  return { totalHours, canAssignHours };
};

const createFooter = <TData,>(key: string) => {
  return ({ table }: { table: Table<TData> }) => {
    const { totalHours, canAssignHours } = getFooterHours(table, key);
    // 0 can-assign hours means no limit, so there is nothing to count down from.
    const unassignedHours = canAssignHours == 0 ? undefined : canAssignHours - totalHours;
    return (
      <>
        <div
          className={`flex p-[10px] text-left text-b3 font-semibold
            ${totalHours > (canAssignHours == 0 ? Infinity : canAssignHours) ? 'text-tertiary-red' : 'text-primary-black-90'}`}
        >
          {totalHours} hours
        </div>
        <div
          className={`flex p-[10px] text-left text-b3 font-semibold
            ${(unassignedHours ?? 0) < 0 ? 'text-tertiary-red' : 'text-primary-black-90'}`}
        >
          {unassignedHours === undefined ? '-' : `${formatNumber(unassignedHours)} hours`}
        </div>
      </>
    );
  };
};

const createCell =
  (key: string) =>
  ({ cell }: CellContext<EditableAcademicTeachingRelatedDutyDetails, unknown>) => {
    const navigate = useNavigate();
    const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);
    const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
    const value: string =
      key != 'academicName'
        ? cell.row.original[key as keyof AcademicTeachingRelatedDutyDetails].toString()
        : `${cell.row.original['lastName']}, ${cell.row.original['firstName']}`;
    return (
      <div className="flex h-[75px] p-[10px] text-left text-b3 font-semibold text-primary-black-90">
        <div className="my-auto">
          {key == 'academicName' ? (
            <div
              onClick={() => {
                setSelectedStaff({
                  id: cell.row.original['academicWorkloadId'],
                  firstName: cell.row.original['firstName'],
                  lastName: cell.row.original['lastName'],
                });
                setActiveSheet('staff-workload');
                navigate('/staffs');
              }}
              className="cursor-pointer text-deep-blue underline"
            >
              {value ?? '-'}
            </div>
          ) : (
            <NumberInput
              key={`${key}-${cell.row.index}`}
              className="bg-white px-[19px] py-3.5 font-normal text-secondary-grey-01 invalid:border invalid:border-red-600 invalid:bg-red-100
                invalid:text-tertiary-red"
              value={parseNumericValue(value)}
              min={0}
              disabled={!cell.row.original.isEditable}
              data-last-value={value}
              data-original-value={value}
              data-index={cell.row.index}
              data-key={key}
              onChange={(sender) => cell.row.original.dataChangeCallback(sender, cell)}
              decimalScale={2}
            />
          )}
        </div>
      </div>
    );
  };

export const teachingRelatedDutiesViewColumns: ColumnDef<EditableAcademicTeachingRelatedDutyDetails>[] = [
  {
    accessorKey: 'academicName',
    header: createHeader('Allocations'),
    cell: createCell('academicName'),
    footer: () => (
      <>
        <div className="flex p-[10px] text-[16px] text-b3 text-secondary-grey-02">Assigned</div>
        <div className="flex p-[10px] text-[16px] text-b3 text-secondary-grey-02">Unassigned</div>
      </>
    ),
  },
  {
    accessorKey: 'unitCoordination',
    header: createHeader('Unit coordination'),
    cell: createCell('trdUnitCoordinationHours'),
    footer: createFooter('unitCoordination'),
  },
  {
    accessorKey: 'marking',
    header: createHeader('Marking'),
    cell: createCell('trdMarkingHours'),
    footer: createFooter('marking'),
  },
  {
    accessorKey: 'preparation',
    header: createHeader('Preparation'),
    cell: createCell('trdPreparationHours'),
    footer: createFooter('preparation'),
  },
  {
    accessorKey: 'coAssessorDuties',
    header: createHeader('Co-assessor duties'),
    cell: createCell('trdCoAssessorDutiesHours'),
    footer: createFooter('coAssessorDuties'),
  },
  {
    accessorKey: 'consultation',
    header: createHeader('Consultation'),
    cell: createCell('trdConsultationHours'),
    footer: createFooter('consultation'),
  },
  {
    accessorKey: 'moderation',
    header: createHeader('Moderation'),
    cell: createCell('trdModerationHours'),
    footer: createFooter('moderation'),
  },
  {
    accessorKey: 'other',
    header: createHeader('Other'),
    cell: createCell('trdOtherHours'),
    footer: createFooter('other'),
  },
  {
    accessorKey: 'unitDevelopment',
    header: createHeader('Unit development'),
    cell: createCell('trdUnitDevelopmentHours'),
    footer: createFooter('unitDevelopment'),
  },
];
